import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { ThemeToggle } from './ThemeToggle';
import { LanguageToggle } from './LanguageToggle';
import { CurrencyToggle } from './CurrencyToggle';
import { ArrowDown, TrendingUp, ShieldCheck, Calculator } from 'lucide-react';

export const HeroSection = ({ onScrollToCalculator }) => {
  const { t, language } = useApp();

  return (
    <section
      className="relative min-h-[auto] flex items-center justify-center py-16 sm:py-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
        {/* Logo */}
        <div className="inline-flex items-center justify-center mb-6">
          <img src="/logo.png" alt="Money Smart" className="h-20 sm:h-28 w-auto" />
        </div>

        {/* Main tagline */}
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-4 leading-relaxed" data-testid="hero-subtitle">
          {t('heroSubtitle')}
        </p>

        {/* CTA */}
        <Button
          size="lg"
          onClick={onScrollToCalculator}
          className="h-12 px-8 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 hover:from-blue-700 hover:to-emerald-600 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 hover:-translate-y-0.5 mb-10"
          data-testid="cta-button"
        >
          {t('ctaButton')}
          <ArrowDown className="ml-2 h-4 w-4 animate-bounce" />
        </Button>

        {/* SEO Content Block */}
        <div className="mt-8 text-left max-w-3xl mx-auto bg-card/60 backdrop-blur-sm border border-border/40 rounded-2xl p-6 sm:p-8 shadow-sm space-y-5">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-snug">
            {language === 'ro'
              ? 'Calculator Dobândă Compusă – Simulează Creșterea Economiilor Tale'
              : 'Compound Interest Calculator – Simulate Your Savings Growth'}
          </h1>

          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            {language === 'ro'
              ? 'Descoperă cum îți pot crește economiile în timp folosind dobânda compusă. Cu ajutorul calculatorului nostru financiar, poți estima rapid cât vei avea în viitor în funcție de suma investită, contribuțiile lunare și randamentul anual.'
              : 'Discover how your savings can grow over time using compound interest. With our financial calculator, you can quickly estimate how much you will have in the future based on the amount invested, monthly contributions and annual return.'}
          </p>

          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              {language === 'ro' ? 'Ce este dobânda compusă?' : 'What is compound interest?'}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {language === 'ro'
                ? 'Dobânda compusă înseamnă că nu câștigi dobândă doar la suma inițială, ci și la dobânda acumulată în timp. Este unul dintre cele mai puternice mecanisme financiare pentru creșterea averii pe termen lung.'
                : 'Compound interest means you earn interest not just on the initial amount, but also on the interest accumulated over time. It is one of the most powerful financial mechanisms for long-term wealth growth.'}
            </p>
          </div>

          <div>
            <h2 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-500" />
              {language === 'ro' ? 'Ce poți calcula cu acest tool?' : 'What can you calculate with this tool?'}
            </h2>
            <ul className="space-y-2">
              {(language === 'ro' ? [
                'Valoarea finală a investiției',
                'Dobânda totală acumulată',
                'Impactul contribuțiilor lunare',
                'Diferența dintre strategii de investiții',
                'Scadențarul unui credit ipotecar sau de consum',
                'Planul de economisire pentru viitorul copilului',
              ] : [
                'Final investment value',
                'Total accumulated interest',
                'Impact of monthly contributions',
                'Difference between investment strategies',
                'Mortgage or consumer loan amortization schedule',
                'Child savings plan projections',
              ]).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-muted-foreground">
                  <span className="mt-1 w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-2 border-t border-border/40">
            <p className="text-xs sm:text-sm text-muted-foreground flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              {language === 'ro'
                ? 'Impozite și comisioane calculate conform legislației din România. Simulator plan de acumulare, viitorul copilului și credit — toate într-un singur loc.'
                : 'Taxes and fees calculated according to Romanian legislation. Accumulation plan, child future, and credit simulator — all in one place.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Header = () => {
  return (
    <header
      className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-border/40 shadow-sm"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/logo.png" alt="Money Smart" className="h-10 sm:h-12 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          <CurrencyToggle />
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

export default HeroSection;
